/**
 * A deploy invalidates every tab that is already open.
 *
 * The bundle is split into hashed chunks and a route asks for its own on first
 * visit. Deploy again and the old hashes are gone - so a tab that has been
 * sitting open since before the deploy asks for a file that no longer exists,
 * the server answers with index.html because that is what it answers for
 * anything it cannot find, and the browser tries to parse HTML as JavaScript:
 *
 *   Uncaught SyntaxError: Unexpected token '<'
 *   Uncaught ChunkLoadError: Loading chunk vendors-...-e03adc failed
 *
 * Nothing is broken on the server. The tab is simply out of date, and the fix
 * is the one the person would have arrived at themselves: reload, which fetches
 * the current index.html and with it the current chunk names.
 *
 * Once per session, and only once. A reload that does not fix it would
 * otherwise become a loop, and a loop is worse than the error - so the flag is
 * set before reloading and cleared only on a load that gets far enough to run
 * this module without failing again. After that one attempt the error boundary
 * takes it and shows something a person can act on.
 */

const FLAG = 'cc-chunk-reload';

/** The shapes this failure actually arrives in. */
const isStaleChunkError = (value: unknown): boolean => {
  const err = value as { name?: string; message?: string } | undefined;
  const name = err?.name ?? '';
  const message = err?.message ?? '';

  if (name === 'ChunkLoadError') return true;

  return (
    // Webpack/rspack, when the chunk 404s or returns the SPA fallback.
    /Loading chunk \S+ failed/i.test(message) ||
    /Loading CSS chunk \S+ failed/i.test(message) ||
    // Native dynamic import, same cause.
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    // The HTML-parsed-as-JavaScript case. Narrowed to the '<' token on purpose:
    // an ordinary syntax error in our own code must not reload the page.
    /Unexpected token '</.test(message)
  );
};

const reloadOnce = (): void => {
  let alreadyTried = true;
  try {
    alreadyTried = sessionStorage.getItem(FLAG) === '1';
    if (!alreadyTried) sessionStorage.setItem(FLAG, '1');
  } catch {
    // Private mode, or storage blocked. Without somewhere to record the
    // attempt there is no way to stop a loop, so do not start one.
    return;
  }

  if (alreadyTried) return;

  // eslint-disable-next-line no-console
  console.warn('[chunk] a chunk failed to load; reloading once to pick up the current bundle');
  window.location.reload();
};

/**
 * Starts listening. Called once, as early as possible, so a chunk that fails
 * during the first render is caught too.
 */
export const installChunkReload = (): void => {
  window.addEventListener('error', (e) => {
    // Covers both a thrown ChunkLoadError and the parse error raised by the
    // script element itself when the response was HTML.
    if (isStaleChunkError(e.error) || (e.message && isStaleChunkError({ message: e.message }))) {
      reloadOnce();
    }
  });

  window.addEventListener('unhandledrejection', (e) => {
    if (isStaleChunkError(e.reason)) reloadOnce();
  });
};

/**
 * Clears the flag once the app is running.
 *
 * The flag exists to stop a reload loop, not to stop a second attempt ever. If
 * the app gets far enough to render, the reload worked - and the next deploy,
 * in this same tab, deserves the same one attempt.
 */
export const chunkReloadSucceeded = (): void => {
  try {
    sessionStorage.removeItem(FLAG);
  } catch {
    /* nothing to clear */
  }
};

export { isStaleChunkError };
